export type Space = 0 | 4 | 8 | 12 | 16 | 24 | 32 | 40 | 48 | 64;
export type Radius = 0 | 8 | 16 | 24 | "full";
// Curated responsive breakpoint scale — the two steps this mobile-first design
// system sanctions: `md` (768) and `lg` (1024). `sm`/`xl`/`2xl` are forbidden repo-wide
// (the `banned-breakpoints` token-lint rule, ADR-0081), so layout primitives express
// responsive changes only on these named steps, never arbitrary px or a wider scale.
export type Breakpoint = "md" | "lg";
export const breakpointOrder: Breakpoint[] = ["md", "lg"];

// ONE responsive-prop shape shared by every layout primitive (ADR-0094): either a
// scalar or an explicit per-breakpoint map on the curated scale — `base` (mobile-first)
// required, each breakpoint optional. Mirrors `Grid.columns`; mature systems (Radix
// Themes, Chakra, Panda) keep a single object shape across all responsive props.
export type Responsive<T extends string | number> =
  | T
  | ({ base: T } & Partial<Record<Breakpoint, T>>);

export type FlexDirection = "row" | "column";
export type AlignValue = "start" | "center" | "end" | "stretch" | "baseline";

export type ResponsiveSpace = Responsive<Space>;
export type ResponsiveDirection = Responsive<FlexDirection>;
export type ResponsiveAlign = Responsive<AlignValue>;

export const gapMap: Record<Space, string> = {
  0: "gap-0",
  4: "gap-1",
  8: "gap-2",
  12: "gap-3",
  16: "gap-4",
  24: "gap-6",
  32: "gap-8",
  40: "gap-10",
  48: "gap-12",
  64: "gap-16",
};
export const mdGapMap: Record<Space, string> = {
  0: "md:gap-0",
  4: "md:gap-1",
  8: "md:gap-2",
  12: "md:gap-3",
  16: "md:gap-4",
  24: "md:gap-6",
  32: "md:gap-8",
  40: "md:gap-10",
  48: "md:gap-12",
  64: "md:gap-16",
};
export const lgGapMap: Record<Space, string> = {
  0: "lg:gap-0",
  4: "lg:gap-1",
  8: "lg:gap-2",
  12: "lg:gap-3",
  16: "lg:gap-4",
  24: "lg:gap-6",
  32: "lg:gap-8",
  40: "lg:gap-10",
  48: "lg:gap-12",
  64: "lg:gap-16",
};
export const gapBreakpointMap: Record<Breakpoint, Record<Space, string>> = {
  md: mdGapMap,
  lg: lgGapMap,
};

export const paddingMap: Record<Space, string> = {
  0: "p-0",
  4: "p-1",
  8: "p-2",
  12: "p-3",
  16: "p-4",
  24: "p-6",
  32: "p-8",
  40: "p-10",
  48: "p-12",
  64: "p-16",
};
export const mdPaddingMap: Record<Space, string> = {
  0: "md:p-0",
  4: "md:p-1",
  8: "md:p-2",
  12: "md:p-3",
  16: "md:p-4",
  24: "md:p-6",
  32: "md:p-8",
  40: "md:p-10",
  48: "md:p-12",
  64: "md:p-16",
};
export const lgPaddingMap: Record<Space, string> = {
  0: "lg:p-0",
  4: "lg:p-1",
  8: "lg:p-2",
  12: "lg:p-3",
  16: "lg:p-4",
  24: "lg:p-6",
  32: "lg:p-8",
  40: "lg:p-10",
  48: "lg:p-12",
  64: "lg:p-16",
};
export const paddingBreakpointMap: Record<Breakpoint, Record<Space, string>> = {
  md: mdPaddingMap,
  lg: lgPaddingMap,
};

export const paddingXMap: Record<Space, string> = {
  0: "px-0",
  4: "px-1",
  8: "px-2",
  12: "px-3",
  16: "px-4",
  24: "px-6",
  32: "px-8",
  40: "px-10",
  48: "px-12",
  64: "px-16",
};
export const mdPaddingXMap: Record<Space, string> = {
  0: "md:px-0",
  4: "md:px-1",
  8: "md:px-2",
  12: "md:px-3",
  16: "md:px-4",
  24: "md:px-6",
  32: "md:px-8",
  40: "md:px-10",
  48: "md:px-12",
  64: "md:px-16",
};
export const lgPaddingXMap: Record<Space, string> = {
  0: "lg:px-0",
  4: "lg:px-1",
  8: "lg:px-2",
  12: "lg:px-3",
  16: "lg:px-4",
  24: "lg:px-6",
  32: "lg:px-8",
  40: "lg:px-10",
  48: "lg:px-12",
  64: "lg:px-16",
};
export const paddingXBreakpointMap: Record<Breakpoint, Record<Space, string>> = {
  md: mdPaddingXMap,
  lg: lgPaddingXMap,
};

export const paddingYMap: Record<Space, string> = {
  0: "py-0",
  4: "py-1",
  8: "py-2",
  12: "py-3",
  16: "py-4",
  24: "py-6",
  32: "py-8",
  40: "py-10",
  48: "py-12",
  64: "py-16",
};
export const mdPaddingYMap: Record<Space, string> = {
  0: "md:py-0",
  4: "md:py-1",
  8: "md:py-2",
  12: "md:py-3",
  16: "md:py-4",
  24: "md:py-6",
  32: "md:py-8",
  40: "md:py-10",
  48: "md:py-12",
  64: "md:py-16",
};
export const lgPaddingYMap: Record<Space, string> = {
  0: "lg:py-0",
  4: "lg:py-1",
  8: "lg:py-2",
  12: "lg:py-3",
  16: "lg:py-4",
  24: "lg:py-6",
  32: "lg:py-8",
  40: "lg:py-10",
  48: "lg:py-12",
  64: "lg:py-16",
};
export const paddingYBreakpointMap: Record<Breakpoint, Record<Space, string>> = {
  md: mdPaddingYMap,
  lg: lgPaddingYMap,
};

export const rowGapMap: Record<Space, string> = {
  0: "gap-y-0",
  4: "gap-y-1",
  8: "gap-y-2",
  12: "gap-y-3",
  16: "gap-y-4",
  24: "gap-y-6",
  32: "gap-y-8",
  40: "gap-y-10",
  48: "gap-y-12",
  64: "gap-y-16",
};
export const mdRowGapMap: Record<Space, string> = {
  0: "md:gap-y-0",
  4: "md:gap-y-1",
  8: "md:gap-y-2",
  12: "md:gap-y-3",
  16: "md:gap-y-4",
  24: "md:gap-y-6",
  32: "md:gap-y-8",
  40: "md:gap-y-10",
  48: "md:gap-y-12",
  64: "md:gap-y-16",
};
export const lgRowGapMap: Record<Space, string> = {
  0: "lg:gap-y-0",
  4: "lg:gap-y-1",
  8: "lg:gap-y-2",
  12: "lg:gap-y-3",
  16: "lg:gap-y-4",
  24: "lg:gap-y-6",
  32: "lg:gap-y-8",
  40: "lg:gap-y-10",
  48: "lg:gap-y-12",
  64: "lg:gap-y-16",
};
export const rowGapBreakpointMap: Record<Breakpoint, Record<Space, string>> = {
  md: mdRowGapMap,
  lg: lgRowGapMap,
};

export const columnGapMap: Record<Space, string> = {
  0: "gap-x-0",
  4: "gap-x-1",
  8: "gap-x-2",
  12: "gap-x-3",
  16: "gap-x-4",
  24: "gap-x-6",
  32: "gap-x-8",
  40: "gap-x-10",
  48: "gap-x-12",
  64: "gap-x-16",
};
export const mdColumnGapMap: Record<Space, string> = {
  0: "md:gap-x-0",
  4: "md:gap-x-1",
  8: "md:gap-x-2",
  12: "md:gap-x-3",
  16: "md:gap-x-4",
  24: "md:gap-x-6",
  32: "md:gap-x-8",
  40: "md:gap-x-10",
  48: "md:gap-x-12",
  64: "md:gap-x-16",
};
export const lgColumnGapMap: Record<Space, string> = {
  0: "lg:gap-x-0",
  4: "lg:gap-x-1",
  8: "lg:gap-x-2",
  12: "lg:gap-x-3",
  16: "lg:gap-x-4",
  24: "lg:gap-x-6",
  32: "lg:gap-x-8",
  40: "lg:gap-x-10",
  48: "lg:gap-x-12",
  64: "lg:gap-x-16",
};
export const columnGapBreakpointMap: Record<Breakpoint, Record<Space, string>> = {
  md: mdColumnGapMap,
  lg: lgColumnGapMap,
};

export const radiusMap: Record<Radius, string> = {
  0: "rounded-none",
  8: "rounded-lg",
  16: "rounded-2xl",
  24: "rounded-3xl",
  full: "rounded-full",
};

// Resolves a responsive value to its space-separated utility classes. A scalar maps
// through `baseMap`; an explicit `{ base, md?, lg? }` map emits the base (unprefixed)
// class plus a prefixed class per specified breakpoint, in mobile-first order.
export function resolveResponsive<T extends string | number>(
  value: Responsive<T>,
  baseMap: Record<T, string>,
  breakpointMap: Record<Breakpoint, Record<T, string>>,
): string {
  if (typeof value !== "object") return baseMap[value];
  const classes = [baseMap[value.base]];
  for (const breakpoint of breakpointOrder) {
    const step = value[breakpoint];
    if (step !== undefined) classes.push(breakpointMap[breakpoint][step]);
  }
  return classes.join(" ");
}
